/**
 * Date utilities — tiny zero-dependency wrappers so we don't pull dayjs into
 * the shared package (which must stay leaf).
 *
 * All server-side code uses UTC; the admin panel renders in Asia/Kuala_Lumpur.
 * Convert at the presentation layer, never inside the ledger.
 */

/** ISO8601 with timezone offset. */
export function toIso(date: Date): string {
  return date.toISOString();
}

/** Parse ISO8601 → Date. Returns null on invalid input. */
export function fromIso(value: string): Date | null {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Unix epoch seconds (not ms) — matches the legacy PHP `time()` return.
 * Used when we need to compare against an existing `create_time` column in
 * the migrated data.
 */
export function unixSeconds(date: Date = new Date()): number {
  return Math.floor(date.getTime() / 1000);
}

/** Compose a YYYY-MM-DD string in UTC. */
export function toDayKey(date: Date = new Date()): string {
  return date.toISOString().slice(0, 10);
}

/** Add minutes — pure, does not mutate. */
export function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60_000);
}

/**
 * Format a UTC ISO string as Beijing wall time `YYYY-MM-DD HH:mm`.
 * Fixed +8 offset — must not depend on the viewer's browser timezone.
 * Returns '—' for null/undefined/invalid input.
 */
export function formatBeijingTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const t = new Date(d.getTime() + 8 * 3_600_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())} ${pad(t.getUTCHours())}:${pad(t.getUTCMinutes())}`;
}
